import React from "react";
import { Button } from "../ui/button";
import { ArrowLeft, Maximize, Minimize, Settings } from "lucide-react";

/**
 * Shared TV Controls component for bottom-left corner.
 * Contains Back, Fullscreen, and Settings buttons.
 *
 * @param {Object} props
 * @param {boolean} props.visible - Whether controls are visible
 * @param {boolean} props.isDark - Dark mode state
 * @param {boolean} props.isFullscreen - Fullscreen state
 * @param {Function} props.onBack - Callback for back button
 * @param {Function} props.onFullscreenToggle - Callback for fullscreen toggle
 * @param {Function} props.onSettingsClick - Callback to open settings dialog
 * @param {React.ReactNode} props.extraControls - Additional controls to render (optional)
 */
export function TVControls({
  visible,
  isDark,
  isFullscreen,
  bottomOffset = "2vh",
  onBack,
  onFullscreenToggle,
  onSettingsClick,
  extraControls,
}) {
  const buttonStyle = {
    width: "4vh",
    height: "4vh",
    borderRadius: "1.2vh",
    // Slightly more opaque bg instead of a backdrop blur: backdrop-filter renders these
    // control buttons as black squares on the Android TV box's WebView GPU.
    background: isDark ? "rgba(40,40,40,0.85)" : "rgba(255,255,255,0.85)",
    border: `2px solid ${isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"}`,
    boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.1)",
  };

  const iconStyle = {
    width: "2.2vh",
    height: "2.2vh",
    color: isDark ? "#fff" : "#1a1a1a",
  };

  return (
    <div
      data-tv-controls
      className="absolute z-[10000] flex transition-opacity duration-300"
      style={{
        bottom: bottomOffset,
        left: "2vh",
        gap: "1vh",
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      {/* Extra controls (e.g., test buttons) */}
      {extraControls}

      {/* Back Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onBack}
        style={buttonStyle}
        className="hover:scale-110 transition-transform duration-200"
        title="Back to Dashboard"
      >
        <ArrowLeft style={iconStyle} />
      </Button>

      {/* Fullscreen Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onFullscreenToggle}
        style={buttonStyle}
        className="hover:scale-110 transition-transform duration-200"
        title={isFullscreen ? "Exit Fullscreen" : "Toggle Fullscreen"}
      >
        {isFullscreen ? (
          <Minimize style={iconStyle} />
        ) : (
          <Maximize style={iconStyle} />
        )}
      </Button>

      {/* Settings Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onSettingsClick}
        style={buttonStyle}
        className="hover:scale-110 transition-transform duration-200"
        title="Display Settings"
      >
        <Settings style={iconStyle} />
      </Button>
    </div>
  );
}

export default TVControls;
